import { observer } from "mobx-react-lite";
import { Tooltip } from "antd";
import { useMst } from "../../stores/main";
import { frameToPixel } from "./utils/timelineHelpers";
import { useSceneTrimDrag } from "./hooks/useSceneTrimDrag";
import { useSceneTrimState } from "./hooks/useSceneTrimState";

interface TimelineSceneTrimBarProps {
  compositionStart: number;
  totalFrames: number;
}

/**
 * Renders the scene trim bar with a draggable right handle.
 * Shows the effective playback range and a greyed/hatched region for trimmed end area.
 */
export const TimelineSceneTrimBar = observer(
  ({ compositionStart, totalFrames }: TimelineSceneTrimBarProps) => {
    const { timelineStore } = useMst();
    const { zoom, scrollOffset } = timelineStore;
    const { currentDuration, baselineDuration } = useSceneTrimState();
    const { isDragging, previewTrimEnd, startDrag } = useSceneTrimDrag(
      compositionStart,
      baselineDuration || totalFrames,
      currentDuration
    );

    // Calculate the effective trim end (use preview during drag, otherwise current duration)
    const effectiveTrimEnd =
      isDragging && previewTrimEnd !== null
        ? previewTrimEnd - compositionStart
        : currentDuration;

    // Calculate pixel positions
    const trimEndX = frameToPixel(effectiveTrimEnd, zoom, scrollOffset);
    const totalFramesX = frameToPixel(
      baselineDuration || totalFrames,
      zoom,
      scrollOffset
    );

    // Check if there's trimming (current duration is less than the loaded baseline)
    const hasTrimming = effectiveTrimEnd < (baselineDuration || totalFrames);
    // Check if there's extension (current duration is greater than the loaded baseline)
    const hasExtension = effectiveTrimEnd > (baselineDuration || totalFrames);
    const handleLabel = "Drag to trim or extend scene duration";

    return (
      <div className="timeline-scene-trim-bar">
        {/* Work area bar showing effective playback range (only when trimmed) */}
        {hasTrimming && (
          <div
            className="timeline-scene-trim-bar__work-area"
            style={{
              left: 0,
              width: trimEndX,
            }}
          />
        )}

        {/* Trimmed end region (hatched/yellow) - only when trimmed */}
        {hasTrimming && (
          <div
            className="timeline-scene-trim-bar__trimmed-region"
            style={{
              left: Math.max(0, trimEndX),
              width: Math.max(0, totalFramesX - trimEndX),
            }}
          />
        )}

        {/* Extended region (hatched/green) - only when extended */}
        {hasExtension && (
          <div
            className="timeline-scene-trim-bar__extended-region"
            style={{
              left: Math.max(0, totalFramesX),
              width: Math.max(0, trimEndX - totalFramesX),
            }}
          />
        )}

        {/* Right handle for adjusting trim end - always visible */}
        <Tooltip title={handleLabel} placement="top">
          <div
            className={`timeline-scene-trim-bar__handle timeline-scene-trim-bar__handle--right ${
              hasTrimming ? "timeline-scene-trim-bar__handle--active" : ""
            } ${
              hasExtension ? "timeline-scene-trim-bar__handle--extended" : ""
            } ${
              trimEndX < 0 ? "timeline-scene-trim-bar__handle--offscreen" : ""
            }`}
            aria-label={handleLabel}
            style={{
              left: trimEndX,
            }}
            onMouseDown={startDrag}
          />
        </Tooltip>
      </div>
    );
  }
);
